import { Component, ElementRef, ViewChild, AfterViewInit, Input, OnDestroy } from '@angular/core';
import SignaturePad from 'signature_pad';
import { ButtonModule } from 'primeng/button';
import { DataService } from '../services/data';
import { Subscription } from 'rxjs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadString, getDownloadURL } from '@firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { Firestore } from '@angular/fire/firestore';
import { SpinnerService } from '../services/spinner.service';
import { ToastService } from '../services/toast.service';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.html',
  styleUrls: ['./signature-pad.css'],
  standalone: true,
  imports: [ButtonModule, CommonModule, TranslateModule],
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasContainer') containerRef!: ElementRef<HTMLDivElement>;
  @Input() firmaGuardadaUrl: string | null = null;

  signaturePad!: SignaturePad;
  signatureImg: string | null = null;
  isSaving = false;
  churchId: string | null = null;
  /** Evita leer el canvas en cada change detection (dispara errores con extensiones SES/MetaMask). */
  padIsEmpty = true;
  padCanUndo = false;
  private subscriptions = new Subscription();
  private resizeObserver?: ResizeObserver;
  private resizeDebounceId: ReturnType<typeof setTimeout> | null = null;

  previewFirmaBase64: string | null = null;
  firmaPendienteBase64: string | null = null;
  loading: boolean = false;

  // Controles de firma
  // Configuración por defecto
  private readonly DEFAULT_LINE_WIDTH = 2;
  private readonly DEFAULT_COLOR = 'rgb(0, 0, 0)';

  constructor(
    private dataService: DataService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.suscripciones();
  }

  ngAfterViewInit() {
    // Deferir init del canvas para evitar conflictos con el layout y extensiones del navegador
    requestAnimationFrame(() => {
      try {
        this.initializeCanvas();
        this.setupResizeObserver();
      } catch {
        // Evitar que errores del canvas (p. ej. interceptados por extensiones SES) propaguen
      }
    });
  }

  private initializeCanvas() {
    const canvas = this.canvasRef?.nativeElement;
    const container = this.containerRef?.nativeElement;

    if (!canvas || !container) return;

    this.updateCanvasSize();

    this.signaturePad = new SignaturePad(canvas, {
      minWidth: this.DEFAULT_LINE_WIDTH - 0.5,
      maxWidth: this.DEFAULT_LINE_WIDTH + 0.5,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      penColor: this.DEFAULT_COLOR,
    });

    this.signaturePad.addEventListener('endStroke', () => this.refreshPadState());
    this.refreshPadState();
  }

  private refreshPadState(): void {
    if (!this.signaturePad) {
      this.padIsEmpty = true;
      this.padCanUndo = false;
      return;
    }
    this.padIsEmpty = this.signaturePad.isEmpty();
    const data = this.signaturePad.toData();
    this.padCanUndo = !!(data?.length);
  }

  private setupResizeObserver() {
    if (!this.containerRef?.nativeElement || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeDebounceId) {
        clearTimeout(this.resizeDebounceId);
      }
      this.resizeDebounceId = setTimeout(() => {
        try {
          if (this.signaturePad) {
            this.resizeCanvas();
            this.refreshPadState();
          }
        } catch {
          // Ignorar errores en resize
        }
      }, 150);
    });

    this.resizeObserver.observe(this.containerRef.nativeElement);
  }

  private updateCanvasSize() {
    const canvas = this.canvasRef.nativeElement;
    const container = this.containerRef?.nativeElement;

    if (!container) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container.offsetWidth;
    const height = Math.max(200, width * 0.4);

    // Solo actualizar si las dimensiones cambiaron
    if (canvas.width !== width * ratio || canvas.height !== height * ratio) {
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext('2d');
      if (context) {
        context.scale(ratio, ratio);
      }
    }
  }

  private resizeCanvas() {
    if (!this.signaturePad || !this.canvasRef?.nativeElement) return;

    const data = this.signaturePad.toData();
    this.updateCanvasSize();

    if (data?.length) {
      this.signaturePad.fromData(data);
    }
  }

  private suscripciones() {
    this.subscriptions.add(
      this.dataService.churchId$.subscribe(churchId => {
        this.churchId = churchId;
      })
    );

    this.subscriptions.add(
      this.dataService.churchSignatureUrl$.subscribe(url => {
        this.firmaGuardadaUrl = url;
        if (url) {
          this.previewFirmaBase64 = url;
        }
      })
    );
  }

  // Deshacer último trazo
  undo() {
    if (!this.signaturePad) return;

    const data = this.signaturePad.toData();
    if (data && data.length > 0) {
      data.pop();
      this.signaturePad.fromData(data);
      this.refreshPadState();
    }
  }

  clearPad() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.signatureImg = null;
      this.refreshPadState();
    }
  }

  async subirFirma() {
    if (!this.signaturePad || this.padIsEmpty) {
      this.toastService.showWarning(this.translate.instant('SETTINGS.SIGN_PAD_TOAST_SIGN_FIRST'));
      return;
    }

    this.loading = true;

    try {
      const storage = getStorage();
      const rutaRef = ref(storage, `iglesias/${this.churchId}/firma.png`);

      const base64png = this.signaturePad.toDataURL('image/png');
      await uploadString(rutaRef, base64png, 'data_url');
      const url = await getDownloadURL(rutaRef);

      const functions = getFunctions();
      const guardarFirma = httpsCallable(functions, 'guardarFirmaDigitalIglesia');
      await guardarFirma({ churchId: this.churchId, url });

      this.signatureImg = url;
      this.dataService.updateChurchSignatureUrl(url);
      this.firmaPendienteBase64 = null;
      this.previewFirmaBase64 = url;

      this.toastService.showSuccess(this.translate.instant('SETTINGS.SIGN_PAD_TOAST_SAVE_OK'));
    } catch (error) {
      this.toastService.showError(this.translate.instant('SETTINGS.SIGN_PAD_TOAST_SAVE_ERROR'));
    } finally {
      this.loading = false;
      this.clearPad();
    }
  }

  ngOnDestroy() {
    if (this.resizeDebounceId) {
      clearTimeout(this.resizeDebounceId);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.subscriptions.unsubscribe();
  }
}